import { type ActivityLogSearchResult, ActivityType } from "@repo/api/client";
import { Button, Flex, Icon, Text, TextField, useTheme } from "@repo/ui";
import { useStyles } from "purse-styles";
import { useState } from "react";
import { toast } from "sonner";
import { activityMeta } from "../../activityLog/activityTypeMeta";
import { useApiClient } from "../../contexts/ApiContext";
import type { TaskId } from "../../db/schema";
import { Routes } from "../../routing";
import { buildTenantPath } from "../../tenant";
import { userFacingMessage } from "../directoryAdmin/directoryAdminUtils";

const MIN_QUERY_LENGTH = 2;

/** Comments have no entry in `activityMeta`; the log shows the author instead. */
const COMMENT_LABELS: Partial<Record<string, string>> = {
	[ActivityType.Comment]: "Comment",
	[ActivityType.EHRComment]: "EHR comment",
};

function activityLabel(type: string | null) {
	const meta = type ? activityMeta[type as ActivityType] : undefined;
	return {
		icon: meta?.icon ?? "MessageSquare",
		color: meta?.color ?? "sand11",
		text: meta?.label ?? (type && COMMENT_LABELS[type]) ?? "Activity",
	} as const;
}

/**
 * One match, as a real link so it can be middle-clicked or tabbed to. Opens in
 * a new tab so the search results stay put.
 */
function SearchResultLink({ result }: { result: ActivityLogSearchResult }) {
	const theme = useTheme();
	const linkStyles = useStyles({
		display: "block",
		color: "inherit",
		textDecoration: "none",
		borderRadius: theme.borderRadiuses.md,
		"& > div": {
			transition: "background-color 0.15s ease, border-color 0.15s ease",
		},
		"&:hover > div, &:focus-visible > div": {
			backgroundColor: theme.colors.sand3,
			borderColor: theme.colors.sandA7,
		},
	});
	const activity = activityLabel(result.activityType);

	return (
		<a
			href={buildTenantPath(Routes.taskDetail(result.taskId as TaskId))}
			target="_blank"
			rel="noopener"
			className={linkStyles}
		>
			<Flex
				column
				gap="xs"
				padding="sm"
				background="sand2"
				borderWidth="md"
				borderColor="sandA5"
				borderRadius="md"
			>
				<Flex justify="space-between" align="center">
					<Flex gap="xxs" align="center">
						<Icon name={activity.icon} color={activity.color} size="sm" />
						<Text color={activity.color} weight="medium" size="sm">
							{activity.text}
						</Text>
					</Flex>
					<Text color="sand10" size="sm">
						{result.createdAt.toLocaleDateString()}
					</Text>
				</Flex>
				<Text size="md" weight="semibold">
					{result.patientName}
				</Text>
				<Flex justify="space-between" align="center" gap="sm">
					<Flex gap="xxs" align="center">
						<Icon name="FileText" size="sm" color="sand10" />
						<Text size="sm">{result.taskTitle}</Text>
					</Flex>
					<Flex gap="xxxs" align="center">
						<Text size="sm" weight="medium" color="accent11">
							Open task
						</Text>
						<Icon name="ChevronRight" size="sm" color="accent11" />
					</Flex>
				</Flex>
				<Text>{result.excerpt}</Text>
			</Flex>
		</a>
	);
}

/**
 * Search the text of every task's activity log, such as a provider name or
 * fax number, to find which tasks mention it. For tenant admins only; the API
 * enforces that.
 */
export function ActivityLogSearchSection() {
	const apiClient = useApiClient();
	const [query, setQuery] = useState("");
	const [searched, setSearched] = useState<{
		results: ActivityLogSearchResult[];
		hasMore: boolean;
	}>();
	const [isSearching, setIsSearching] = useState(false);

	const canSearch = query.trim().length >= MIN_QUERY_LENGTH && !isSearching;

	const handleSearch = async () => {
		setIsSearching(true);
		try {
			setSearched(await apiClient.admin.searchActivityLogs({ query }));
		} catch (error) {
			// Not the error itself: it can carry the search, which may be a
			// patient's name.
			console.error("Failed to search activity logs");
			toast.error(
				userFacingMessage(
					error,
					"We couldn't search the activity logs. Please try again",
				),
			);
		} finally {
			setIsSearching(false);
		}
	};

	return (
		<Flex column gap="md">
			<Text size="lg" weight="semibold" color="sand12">
				Search Activity Logs
			</Text>
			<Text size="sm" color="sand11">
				Find tasks whose activity log mentions a provider, fax number, patient
				or anything else. Shows the newest 50 matches.
			</Text>

			<Flex gap="sm" align="center">
				<Flex column flex>
					<TextField
						aria-label="Search activity logs"
						placeholder="Provider name, fax number, any text"
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === "Enter" && canSearch) {
								void handleSearch();
							}
						}}
						subtlePlaceholder={false}
					/>
				</Flex>
				<Button
					variant="outline"
					size="sm"
					isDisabled={!canSearch}
					onPress={handleSearch}
				>
					<Flex gap="xs" align="center">
						<Icon name="Search" size="sm" />
						<Text size="sm">{isSearching ? "Searching..." : "Search"}</Text>
					</Flex>
				</Button>
			</Flex>

			{searched ? (
				searched.results.length === 0 ? (
					<Text size="sm" color="sand11">
						No activity matches. Try another spelling or part of the name.
					</Text>
				) : (
					<Flex column gap="sm">
						{searched.results.map((result) => (
							<SearchResultLink key={result.id} result={result} />
						))}
						{searched.hasMore ? (
							<Text size="sm" color="sand11">
								Showing the newest 50 matches. Search for something more
								specific to see older ones.
							</Text>
						) : null}
					</Flex>
				)
			) : null}
		</Flex>
	);
}
